<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
  >
    <div class="w-[572px] max-w-[95vw] bg-white rounded-xl shadow flex flex-col">
      <div
        class="p-3 bg-slate-100 rounded-tl-xl rounded-tr-xl border-b border-slate-200 flex justify-between items-center"
      >
        <div class="text-neutral-900 text-lg font-semibold">Upload Files</div>
        <button
          @click="closeModal"
          :disabled="uploading"
          class="p-1 bg-gray-400 rounded-[20px] flex justify-center items-center"
        >
          <svg
            class="w-4 h-4 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>

      <div class="p-4 flex flex-col gap-4">
        <!-- Drop zone -->
        <label
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="handleDrop"
          :class="[
            'w-full p-6 border-2 border-dashed rounded-lg flex flex-col items-center gap-2 cursor-pointer',
            dragging ? 'border-blue_primary bg-blue-50' : 'border-slate-200 bg-slate-50',
          ]"
        >
          <span class="text-slate-600 text-sm font-medium">
            Drag files here or click to select
          </span>
          <span class="text-slate-400 text-xs text-center">
            Up to {{ maxFiles }} files, 25 MB each.
            {{ allowedLabel }}
          </span>
          <input
            ref="fileInput"
            type="file"
            multiple
            :accept="accept"
            class="hidden"
            @change="handleFileChange"
          />
        </label>

        <!-- Selected files -->
        <div v-if="selected.length" class="flex flex-col gap-2 max-h-64 overflow-y-auto">
          <div
            v-for="(item, index) in selected"
            :key="item.key"
            class="flex items-center justify-between gap-3 px-3 py-2 border border-slate-200 rounded-lg"
          >
            <div class="flex flex-col min-w-0">
              <span class="text-slate-600 text-sm truncate">{{ item.file.name }}</span>
              <span
                :class="item.error ? 'text-red-600' : 'text-slate-400'"
                class="text-xs"
              >
                {{ item.error || formatSize(item.file.size) }}
              </span>
            </div>
            <button
              @click="removeFile(index)"
              :disabled="uploading"
              class="text-slate-400 hover:text-red-600 text-sm"
            >
              Remove
            </button>
          </div>
        </div>

        <div v-if="tooManyFiles" class="text-red-600 text-sm">
          You can upload up to {{ maxFiles }} files at a time.
        </div>
      </div>

      <div class="flex justify-end gap-4 p-4">
        <button
          @click="closeModal"
          :disabled="uploading"
          class="px-4 py-2 bg-gray-300 rounded-lg"
        >
          Cancel
        </button>
        <button
          @click="upload"
          :disabled="!canUpload"
          :class="canUpload ? 'bg-blue_primary' : 'bg-slate-300 cursor-not-allowed'"
          class="px-4 py-2 text-white rounded-lg"
        >
          {{ uploading ? "Uploading..." : "Upload" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import {
  MAX_FILES_PER_UPLOAD,
  ALLOWED_EXTENSIONS,
  ACCEPT_ATTRIBUTE,
  validateFile,
  formatSize,
} from "./file-rules";

export default {
  props: {
    isOpen: Boolean,
    projectId: [String, Number],
  },
  emits: ["close", "uploaded"],

  setup(props, { emit }) {
    const store = useStore();
    const fileInput = ref(null);
    const selected = ref([]);
    const dragging = ref(false);
    const uploading = ref(false);
    let keyCounter = 0;

    const validFiles = computed(() =>
      selected.value.filter((item) => !item.error).map((item) => item.file)
    );
    const tooManyFiles = computed(
      () => validFiles.value.length > MAX_FILES_PER_UPLOAD
    );
    const canUpload = computed(
      () => !uploading.value && validFiles.value.length > 0 && !tooManyFiles.value
    );

    function addFiles(fileList) {
      for (const file of Array.from(fileList)) {
        selected.value.push({
          key: keyCounter++,
          file,
          error: validateFile(file),
        });
      }
    }

    function handleFileChange(event) {
      addFiles(event.target.files);
      // Permite volver a elegir el mismo archivo después de quitarlo
      event.target.value = "";
    }

    function handleDrop(event) {
      dragging.value = false;
      addFiles(event.dataTransfer.files);
    }

    function removeFile(index) {
      selected.value.splice(index, 1);
    }

    function closeModal() {
      if (uploading.value) return;
      emit("close");
    }

    async function upload() {
      if (!canUpload.value) return;
      uploading.value = true;
      try {
        const response = await store.dispatch("uploadProjectFiles", {
          id_project: props.projectId,
          files: validFiles.value,
        });
        if (response.ok) {
          emit("uploaded", response.files);
          emit("close");
        }
      } catch (error) {
        store.commit("setSnackbar", {
          text: "Unexpected error while uploading files",
          enabled: true,
        });
      } finally {
        uploading.value = false;
      }
    }

    // Limpia la selección cada vez que se abre el modal
    watch(
      () => props.isOpen,
      (open) => {
        if (open) {
          selected.value = [];
          dragging.value = false;
        }
      }
    );

    return {
      fileInput,
      selected,
      dragging,
      uploading,
      tooManyFiles,
      canUpload,
      maxFiles: MAX_FILES_PER_UPLOAD,
      accept: ACCEPT_ATTRIBUTE,
      allowedLabel: ALLOWED_EXTENSIONS.join(", ").toUpperCase(),
      handleFileChange,
      handleDrop,
      removeFile,
      closeModal,
      upload,
      formatSize,
    };
  },
};
</script>

<style scoped>
</style>
